import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Trophy, Heart, MessageCircle, ThumbsUp, Crown, Medal,
  Award, DollarSign, Star, ChevronRight, Users
} from 'lucide-react';

const RANK_STYLES = {
  1: { color: '#FFD700', icon: Crown, bg: 'bg-amber-500/10' },
  2: { color: '#C0C0C0', icon: Medal, bg: 'bg-muted/25' },
  3: { color: '#CD7F32', icon: Award, bg: 'bg-orange-500/10' },
};

function formatPicture(pic) {
  if (!pic) return null;
  if (pic.startsWith('http')) return pic;
  return `${window.location.origin}${pic}`;
}

export function TopTippersLeaderboard({ creatorUserId }) {
  const [tippers, setTippers] = useState([]);
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!creatorUserId) return;
    setLoading(true);
    fetch(`${API}/leaderboards/top-tippers/${creatorUserId}?period=${period}&limit=10`)
      .then(r => r.json())
      .then(d => setTippers(d.tippers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [creatorUserId, period]);

  if (loading && tippers.length === 0) return null;
  if (!tippers.length) return null;

  return (
    <Card className="p-4 bg-card border-border" data-testid="top-tippers-leaderboard">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-amber-400" /> Top Tippers
        </h3>
        <div className="flex gap-1">
          {['week', 'month', 'all'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`tippers-period-${p}`}
              className={`px-2 py-0.5 rounded text-xs transition-all ${period === p ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground/80 hover:text-muted-foreground'}`}>
              {p === 'all' ? 'All' : p === 'month' ? '30d' : '7d'}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        {tippers.map(t => {
          const style = RANK_STYLES[t.rank] || { color: '#6B7280', bg: 'bg-muted/30' };
          const RankIcon = style.icon || Star;
          return (
            <div key={t.user_id} onClick={() => t.handle && navigate(`/u/${t.handle}`)}
              className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted/20 transition-colors cursor-pointer" data-testid={`tipper-rank-${t.rank}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${style.bg}`}>
                {t.rank <= 3 ? <RankIcon className="w-3.5 h-3.5" style={{ color: style.color }} /> :
                  <span className="text-xs font-bold text-muted-foreground">#{t.rank}</span>}
              </div>
              <div className="w-8 h-8 rounded-full overflow-hidden bg-muted shrink-0">
                {t.picture ? <img src={formatPicture(t.picture)} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground/80">{(t.name || '?')[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.tip_count} tips</p>
              </div>
              <span className="text-sm font-bold text-amber-400">${t.total_tips.toFixed(2)}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export function TopFansLeaderboard({ creatorUserId }) {
  const [fans, setFans] = useState([]);
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!creatorUserId) return;
    setLoading(true);
    fetch(`${API}/leaderboards/top-fans/${creatorUserId}?period=${period}&limit=10`)
      .then(r => r.json())
      .then(d => setFans(d.fans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [creatorUserId, period]);

  if (loading && fans.length === 0) return null;
  if (!fans.length) return null;

  return (
    <Card className="p-4 bg-card border-border" data-testid="top-fans-leaderboard">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Users className="w-4 h-4 text-violet-400" /> Top Fans
        </h3>
        <div className="flex gap-1">
          {['week', 'month', 'all'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} data-testid={`fans-period-${p}`}
              className={`px-2 py-0.5 rounded text-xs transition-all ${period === p ? 'bg-violet-500/20 text-violet-400' : 'text-muted-foreground/80 hover:text-muted-foreground'}`}>
              {p === 'all' ? 'All' : p === 'month' ? '30d' : '7d'}
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5">
        {fans.map(f => {
          const style = RANK_STYLES[f.rank] || { color: '#6B7280', bg: 'bg-muted/30' };
          const RankIcon = style.icon || Star;
          return (
            <div key={f.user_id} onClick={() => f.handle && navigate(`/u/${f.handle}`)}
              className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-muted/20 transition-colors cursor-pointer" data-testid={`fan-rank-${f.rank}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${style.bg}`}>
                {f.rank <= 3 ? <RankIcon className="w-3.5 h-3.5" style={{ color: style.color }} /> :
                  <span className="text-xs font-bold text-muted-foreground">#{f.rank}</span>}
              </div>
              <div className="w-8 h-8 rounded-full overflow-hidden bg-muted shrink-0">
                {f.picture ? <img src={formatPicture(f.picture)} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground/80">{(f.name || '?')[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{f.name}</p>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {f.tips > 0 && <span className="flex items-center gap-0.5"><DollarSign className="w-3 h-3" />{f.tips.toFixed(0)}</span>}
                  {f.comments > 0 && <span className="flex items-center gap-0.5"><MessageCircle className="w-3 h-3" />{f.comments}</span>}
                  {f.likes > 0 && <span className="flex items-center gap-0.5"><ThumbsUp className="w-3 h-3" />{f.likes}</span>}
                </div>
              </div>
              <Badge className="bg-violet-500/10 text-violet-400 text-xs">{f.score.toFixed(0)} pts</Badge>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
